import React, { useState, useEffect } from 'react';
import { 
  X, MessageSquare, FileCheck, Link2, Search, AlertTriangle,
  ChevronRight, Building2, FolderKanban, Briefcase, Layers
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import CommentsPanel from './CommentsPanel';

/**
 * QuickActionModal - Modal for quick actions (Add Comment, Request Decision, Flag Dependency)
 * Used in Executive Cockpit and other dashboards
 */
const QuickActionModal = ({ 
  isOpen, 
  onClose, 
  actionType, // 'comment' | 'decision' | 'dependency'
  tenantId,
  preselectedEntity = null 
}) => {
  const { user } = useAuth();
  const [step, setStep] = useState(preselectedEntity ? 2 : 1); // 1 = select entity, 2 = action form
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(preselectedEntity);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Form state for decision/dependency
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'Medium',
    dueDate: '',
    // For dependency
    dependencyType: 'blocks',
    targetEntity: null
  });

  useEffect(() => {
    if (isOpen) {
      setStep(preselectedEntity ? 2 : 1);
      setSelectedEntity(preselectedEntity);
      setSearchQuery('');
      setSearchResults([]);
      setError(null);
      setSuccess(false);
      setFormData({
        title: '',
        description: '',
        priority: 'Medium',
        dueDate: '',
        dependencyType: 'blocks',
        targetEntity: null
      });
    }
  }, [isOpen, preselectedEntity]);

  const searchEntities = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.globalSearch(query, null, 10);
      setSearchResults(res.data.results || []);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    searchEntities(value);
  };

  const selectEntity = (entity) => {
    setSelectedEntity(entity);
    setStep(2);
  };

  const getEntityIcon = (type) => {
    const icons = {
      portfolio: Building2,
      program: Briefcase,
      project: FolderKanban,
      epic: Layers,
      feature: Layers,
      story: Layers,
      task: Layers
    };
    return icons[type?.toLowerCase()] || Layers;
  };

  const getActionConfig = () => {
    switch (actionType) {
      case 'comment':
        return {
          title: 'Add Comment',
          icon: MessageSquare,
          color: 'var(--clr-brand-primary)',
          description: 'Add a comment to an entity'
        };
      case 'decision':
        return {
          title: 'Request Decision',
          icon: FileCheck,
          color: '#F59E0B',
          description: 'Request a decision from stakeholders'
        };
      case 'dependency':
        return {
          title: 'Flag Dependency',
          icon: Link2,
          color: '#8B5CF6',
          description: 'Flag a dependency between entities'
        };
      default:
        return { title: 'Action', icon: AlertTriangle, color: '#666' };
    }
  };

  const config = getActionConfig();
  const Icon = config.icon;

  const handleSubmitDecision = async () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.createDecisionNew({
        title: formData.title,
        description: formData.description,
        priority: formData.priority,
        due_date: formData.dueDate || null,
        status: 'Pending',
        project_id: selectedEntity?.project_id || selectedEntity?.id,
        links: selectedEntity ? [{
          entity_type: selectedEntity.type?.toLowerCase(),
          entity_id: selectedEntity.id
        }] : []
      });
      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      console.error('Failed to create decision:', err);
      setError('Failed to create decision');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitDependency = async () => {
    if (!formData.title.trim()) {
      setError('Description is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.createDependency({
        name: formData.title,
        description: formData.description,
        dependency_type: formData.dependencyType,
        status: 'Open',
        source_type: selectedEntity?.type?.toLowerCase(),
        source_id: selectedEntity?.id,
        target_type: formData.targetEntity?.type?.toLowerCase(),
        target_id: formData.targetEntity?.id
      });
      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      console.error('Failed to create dependency:', err);
      setError('Failed to create dependency');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${config.color}20` }}
              >
                <Icon className="w-5 h-5" style={{ color: config.color }} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{config.title}</h2>
                <p className="text-sm text-gray-500">{config.description}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5">
            {/* Step 1: Select Entity */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select an entity
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearch}
                      placeholder="Search projects, epics, features..."
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-transparent"
                      data-testid="quick-action-search-input"
                      autoFocus
                    />
                  </div>
                </div>

                {loading && (
                  <div className="text-center py-8 text-gray-500">
                    Searching...
                  </div>
                )}

                {!loading && searchResults.length > 0 && (
                  <div className="space-y-2">
                    {searchResults.map((result) => {
                      const ResultIcon = getEntityIcon(result.type);
                      return (
                        <button
                          key={`${result.type}-${result.id}`}
                          onClick={() => selectEntity(result)}
                          className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left"
                        >
                          <ResultIcon className="w-5 h-5 text-gray-500" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{result.name}</p>
                            <p className="text-sm text-gray-500 capitalize">{result.type}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </button>
                      );
                    })}
                  </div>
                )}

                {!loading && searchQuery.length >= 2 && searchResults.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No results found
                  </div>
                )}

                {!loading && searchQuery.length < 2 && (
                  <div className="text-center py-8 text-gray-400">
                    Type at least 2 characters to search
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Action Form */}
            {step === 2 && (
              <div className="space-y-4">
                {/* Selected Entity */}
                {selectedEntity && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    {(() => {
                      const EntityIcon = getEntityIcon(selectedEntity.type);
                      return <EntityIcon className="w-5 h-5 text-gray-500" />;
                    })()}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{selectedEntity.name}</p>
                      <p className="text-sm text-gray-500 capitalize">{selectedEntity.type}</p>
                    </div>
                    {!preselectedEntity && (
                      <button
                        onClick={() => setStep(1)}
                        className="text-sm text-[var(--clr-brand-primary)] hover:underline"
                      >
                        Change
                      </button>
                    )}
                  </div>
                )}

                {/* Comment Form */}
                {actionType === 'comment' && (
                  <CommentsPanel
                    entityType={selectedEntity?.type?.toLowerCase()}
                    entityId={selectedEntity?.id}
                    tenantId={tenantId || user?.tenant?.id || user?.tenant_id}
                    onCommentAdded={() => {
                      setSuccess(true);
                      setTimeout(() => onClose(), 1000);
                    }}
                  />
                )}

                {/* Decision Form */}
                {actionType === 'decision' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Decision Title *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="What decision needs to be made?"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Provide context for the decision..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Priority
                        </label>
                        <select
                          value={formData.priority}
                          onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        >
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                          <option value="Critical">Critical</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Due Date
                        </label>
                        <input
                          type="date"
                          value={formData.dueDate}
                          onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Dependency Form */}
                {actionType === 'dependency' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dependency Description *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Describe the dependency..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Dependency Type
                      </label>
                      <select
                        value={formData.dependencyType}
                        onChange={(e) => setFormData({ ...formData, dependencyType: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="blocks">Blocks</option>
                        <option value="depends_on">Depends On</option>
                        <option value="relates_to">Relates To</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Details
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Any additional context..."
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                      />
                    </div>
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                {/* Success */}
                {success && (
                  <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm">
                    {actionType === 'comment' ? 'Comment added!' : 
                     actionType === 'decision' ? 'Decision request created!' : 
                     'Dependency flagged!'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {step === 2 && actionType !== 'comment' && !success && (
            <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={actionType === 'decision' ? handleSubmitDecision : handleSubmitDependency}
                disabled={submitting}
                className="px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: config.color }}
              >
                {submitting ? 'Submitting...' : actionType === 'decision' ? 'Submit Decision' : 'Flag Dependency'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default QuickActionModal;
